import React from "react";

import { useFormikContext } from "formik";
import i18n, { t } from "i18next";

import { cn } from "@/utils/cn";

import { TInvoiceFormValues } from "@/types";

import { computeInvoiceTotals, formatMoney } from "@/utils";

/** Subtotal, VAT by rate, total — live from the form's lines. */
export const InvoiceTotals: React.FC<{ className?: string }> = ({
	className,
}) => {
	const { values } = useFormikContext<TInvoiceFormValues>();
	const language = i18n.language;
	const { totalHt, totalVat, totalTtc, vatBreakdown } = computeInvoiceTotals(
		values.lines
	);
	const money = (amount: number) =>
		formatMoney(amount, values.currency, language);
	const percent = (rate: number) =>
		new Intl.NumberFormat(language, {
			style: "percent",
			maximumFractionDigits: 2,
		}).format(rate / 100);

	return (
		<dl
			className={cn(["ml-auto w-full max-w-xs text-sm", className])}
			data-testid="invoice-totals"
		>
			<div className="flex justify-between py-1">
				<dt className="text-neutral-600">{t("invoices.totals.ht")}</dt>
				<dd className="tabular-nums">{money(totalHt)}</dd>
			</div>
			{vatBreakdown.map((row) => (
				<div key={row.rate} className="flex justify-between py-1">
					<dt className="text-neutral-600">
						{t("invoices.totals.vatAt", {
							rate: percent(row.rate),
						})}
					</dt>
					<dd className="tabular-nums">{money(row.vat)}</dd>
				</div>
			))}
			{vatBreakdown.length > 1 && (
				<div className="flex justify-between py-1">
					<dt className="text-neutral-600">
						{t("invoices.totals.vat")}
					</dt>
					<dd className="tabular-nums">{money(totalVat)}</dd>
				</div>
			)}
			<div className="mt-1 flex justify-between border-t border-neutral-300 pt-2 text-base font-semibold">
				<dt>{t("invoices.totals.ttc")}</dt>
				<dd className="tabular-nums">{money(totalTtc)}</dd>
			</div>
		</dl>
	);
};
